import { Button } from "../ui/button";
import mailbox from "@/assets/mailbox.svg";
import { useModal } from "@/hooks/useModal";
import { useEffect } from "react";
import { requestNotificationAuthorization } from "@/bridge/requestNotificationAuthorization";
import { callFirebaseFunction } from "@/bridge/callFirebaseFunction";
import { Timestamp } from "firebase/firestore";
import type { FirebaseTimestamp } from "@skillscratch/shared-types";
import posthog from "posthog-js";
  
export type PushPreferences = "Daily" | "Weekly" | "Only about free money" | "Never";

type UpdateUserPrivateInfoRequest = {
    updates: Partial<{
      lastAskedForPushNotifications?: FirebaseTimestamp;
      pushNotificationsEnabled?: boolean;
      pushPreferences: PushPreferences;
    }>;
};
  
type UpdateUserPrivateInfoResponse = {
    success: boolean;
};

export function NotificationModal() {
    const { closeModal } = useModal();

    useEffect(() => {
        const updateLastAskedTimestamp = async () => {
            callFirebaseFunction<
                UpdateUserPrivateInfoRequest,
                UpdateUserPrivateInfoResponse
            >(
                "updateUserPrivateInfo",
                { 
                    updates: { 
                        lastAskedForPushNotifications: Timestamp.fromDate(new Date())
                    } 
                }
            );
        };
        
        updateLastAskedTimestamp();
    }, []);

    const handlePreferenceSelection = (preference: PushPreferences) => {
        try {
            posthog.capture("push_preference", {
                pushPreference: preference,
            });

            // Request notification permission for all preferences except "Never"
            if (preference !== "Never") {
                requestNotificationAuthorization(preference);
            }

            callFirebaseFunction<
                UpdateUserPrivateInfoRequest,
                UpdateUserPrivateInfoResponse
            >(
                "updateUserPrivateInfo",
                { 
                    updates: { 
                        pushNotificationsEnabled: preference !== "Never",
                        pushPreferences: preference
                    } 
                }
            );
            
            closeModal();
        } catch (error) {
            console.error("Failed to update push preferences:", error);
        }
    };

    return (
        <div className="bg-white rounded-xl shadow-lg py-8 px-2 w-80 text-center">
            <img 
                src={mailbox}
                alt='notifications'
                className="h-16 mx-auto mb-5"
            />
            {/* Text Content */}
            <h2 className="text-blue-dark text-xl font-semibold mb-8 mx-4">How often would you like to receive updates?</h2>
            
            {/* Preference Buttons */}
            <div className="flex flex-col gap-3 mx-4">
                <Button 
                    variant='wallet-btn' 
                    size='walletBtn' 
                    className="bg-blue text-white"
                    onClick={() => handlePreferenceSelection("Daily")}>
                    Daily Updates
                </Button>
                <Button 
                    variant='wallet-btn' 
                    size='walletBtn' 
                    className="bg-blue text-white"
                    onClick={() => handlePreferenceSelection("Weekly")}>
                    Weekly Updates
                </Button>
                <Button 
                    variant='wallet-btn' 
                    size='walletBtn'
                    shine
                    className="bg-gradient-to-r from-green-400 to-green-500 text-white font-bold"
                    onClick={() => handlePreferenceSelection("Only about free money")}>
                    Only Free Money Alerts
                </Button>
                <Button 
                    variant='wallet-btn' 
                    size='walletBtn' 
                    className="bg-gray-decline text-white"
                    onClick={() => handlePreferenceSelection("Never")}>
                    Never
                </Button>
            </div>
        </div>
    );
}